/**
 * 完了した GWS 発行タスクから、初期パスワードを再発行して 1 回だけ見せる(HIDE 2026-10-21)。
 *
 * 発行時のパスワードは申請者へメールで 1 回だけ渡して捨てている(サーバーは保存しない)。
 * 取り出せないときは ID管理(idp)の API で**新しい値に置き換える**しかない。
 * 値は state にだけ持ち、「閉じた(控えた)」で消す。再表示はしない(もう一度押せば再発行)。
 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Modal, Space, Typography, message } from "antd";
import { useLayoutEffect, useRef, useState } from "react";

import type { ResetInitialPasswordResult, WfTask } from "../../lib/api";
import { hasRole } from "../../lib/roles";
import { useApi, useMe } from "../../lib/useApi";
import { errorText } from "./hooks";

const { Text, Paragraph } = Typography;

export function ResetInitialPassword({ task }: { task: WfTask }) {
  const api = useApi();
  const me = useMe();
  const allowed = hasRole(me.data, "idp:operator");
  const employeeId = task.employee_id ?? 0;
  const emp = useQuery({
    queryKey: ["hr", "employee", employeeId],
    queryFn: () => api.employee(employeeId),
    enabled: allowed && employeeId > 0,
  });
  const [shown, setShown] = useState<ResetInitialPasswordResult | null>(null);
  const [isPending, setIsPending] = useState(false);
  const locked = useRef(false);
  const confirmation = useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const generation = useRef(0);

  useLayoutEffect(() => {
    return () => {
      // 対象の切替・アンマウント後は、古い確認も通信結果も使わない。
      generation.current += 1;
      confirmation.current?.destroy();
      confirmation.current = null;
      locked.current = false;
      setShown(null);
      setIsPending(false);
    };
  }, [employeeId, allowed]);

  const reset = async (targetEmployeeId: number, confirmedGeneration: number) => {
    if (confirmedGeneration !== generation.current) return;
    setIsPending(true);
    try {
      // 秘密を MutationCache に残さない。応答はこのコンポーネントの state だけに持つ。
      const res = await api.resetInitialPassword("gws", targetEmployeeId);
      if (confirmedGeneration === generation.current) setShown(res);
    } catch (e) {
      if (confirmedGeneration === generation.current) {
        locked.current = false;
        void message.error(errorText(e));
      }
    } finally {
      if (confirmedGeneration === generation.current) {
        confirmation.current = null;
        setIsPending(false);
      }
    }
  };

  if (!allowed) {
    return (
      <Text type="secondary" data-testid="reset-pw-no-role">
        再発行には ID管理の操作権限(idp:operator)が要る
      </Text>
    );
  }

  const confirm = () => {
    // state の更新を待たずにロックする(確認中・通信中・結果表示中を通して 1 件だけ)。
    if (locked.current || employeeId <= 0) return;
    locked.current = true;
    const target = { employeeId, email: emp.data?.email, name: task.employee_name };
    const who = target.email || target.name || `従業員 #${target.employeeId}`;
    const confirmedGeneration = generation.current;
    let submitted = false;
    confirmation.current = Modal.confirm({
      title: "初期パスワードを再発行する",
      okText: "再発行する",
      okButtonProps: { danger: true },
      cancelText: "やめる",
      maskClosable: false,
      keyboard: false,
      closable: false,
      content: `${who} の初期パスワードを新しい値に置き換える。本人が既に変更したパスワードも無効になる。続けるか?`,
      onOk: () => {
        if (submitted || confirmedGeneration !== generation.current) return;
        submitted = true;
        confirmation.current?.update({ cancelButtonProps: { disabled: true } });
        return reset(target.employeeId, confirmedGeneration);
      },
      onCancel: () => {
        if (!submitted && confirmedGeneration === generation.current) {
          generation.current += 1;
          confirmation.current = null;
          locked.current = false;
        }
      },
    });
  };

  return (
    <>
      <Button onClick={confirm} loading={isPending} data-testid="reset-pw">
        初期パスワードを再発行して表示
      </Button>
      <Modal
        open={shown != null}
        title={`${shown?.email ?? ""} の初期パスワード(この1回だけ)`}
        width={520}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={
          <Button
            type="primary"
            onClick={() => {
              setShown(null);
              locked.current = false;
            }}
          >
            閉じた(控えた)
          </Button>
        }
        destroyOnClose
      >
        {shown && (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text type="secondary">メール</Text>
            <Paragraph copyable={{ text: shown.email }} style={{ marginBottom: 8 }}>
              {shown.email}
            </Paragraph>
            <Text type="secondary">パスワード</Text>
            <Paragraph copyable={{ text: shown.password }} style={{ wordBreak: "break-all" }}>
              <Text code data-testid="reset-pw-value">
                {shown.password}
              </Text>
            </Paragraph>
            {shown.change_at_next_login && (
              <Alert type="info" showIcon message="次回ログイン時に変更が必要" />
            )}
            <Text type="secondary">
              この画面を閉じると二度と表示できない。本人に安全な方法で渡す(もう一度押すと別の値で再発行する)。
            </Text>
          </Space>
        )}
      </Modal>
    </>
  );
}
